import { useState, useEffect, useContext } from 'react';
import { graphqlFetch } from '@shared/api';
import { collectionQueryDocument } from './Collection.document';
import { useQuery } from '@tanstack/react-query';

import config from '@/config';
import { AppContext } from '@/App.context';
import { isNumeric, Issue, USER_ROLE } from '@/constants';
import { useIssueNotFound } from '@shared/lib/hooks';
import { getQueryCacheOptions } from '@shared/query-client/QueryClient';

export function useGetCollection(id: string): GetCollectionResponse {
	const {
		parseGraphQLClientError,
		onlineStatus: isOnline,
	} = useContext(AppContext);
	const [issue, setIssue] = useState<Issue | null>();

	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';
	const cacheOptions = getQueryCacheOptions({
		isOnline,
		isAdmin,
		ttlMinutes: config.app?.cache?.ttl?.getIssue,
	});

	const queryKey = ['collections', id, 'issue'];

	// Cast: same as Preview.hooks.ts's PreviewIssue migration -- the
	// generated type's nullability is honest where the shared Issue type
	// claims required fields; reconciling that is topic 5's "derive
	// Issue/Article/Style from generated types" step. Behaviour unchanged.
	const { isPending, error, data } = useQuery<CollectionQuery>({
		queryKey,
		queryFn: async () =>
			graphqlFetch(collectionQueryDocument, {
				id,
				isSlug: !isNumeric(id),
			}) as unknown as Promise<CollectionQuery>,
		staleTime: cacheOptions.staleTime,
		refetchInterval: cacheOptions.refetchInterval,
		gcTime: cacheOptions.gcTime,
	});

	useIssueNotFound(issue);

	useEffect(() => {
		if (!data) return;

		const { issue } = data;
		setIssue(issue);
	}, [data]);

	return {
		isLoading: isPending,
		error: error ? parseGraphQLClientError(error) : null,
		issue,
	};
}

interface CollectionQuery {
	issue: Issue | null;
}

interface GetCollectionResponse {
	isLoading: boolean;
	error: Error | null;
	issue?: Issue | null;
}
